import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Button } from '../../shared/components/Button';
import { Field, Input, Textarea } from '../../shared/components/Field';
import { Alert, EmptyState, Spinner } from '../../shared/components/feedback';
import { Switch } from '../../shared/components/Switch';
import { PlusIcon } from '../../shared/components/icons';
import { useActiveAccount } from '../../account/ActiveAccountContext';
import { useDialog } from '../../shared/dialog/DialogProvider';
import { ApiError } from '../../api/client';
import * as youtubeTopicApi from '../../api/youtubeTopic';
import type { YoutubeTopic, YoutubeTopicUpsertRequest, YoutubeVideoHit } from '../../api/youtubeTopic';

const EMPTY_FORM: YoutubeTopicUpsertRequest = {
  name: '',
  keyword: '',
  pinOwnReplyEnabled: false,
  commentBrief: '',
};

type PanelMode = 'idle' | 'create' | 'detail';

export function YoutubeTopicsPanel() {
  const { accounts } = useActiveAccount();
  const { alert, confirm } = useDialog();

  const [topics, setTopics] = useState<YoutubeTopic[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<PanelMode>('idle');
  const [form, setForm] = useState<YoutubeTopicUpsertRequest>(EMPTY_FORM);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [assignedAccountIds, setAssignedAccountIds] = useState<number[]>([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSavingId, setAssignmentSavingId] = useState<number | null>(
    null
  );
  const [videos, setVideos] = useState<YoutubeVideoHit[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState<string | null>(null);
  const [searchedKeyword, setSearchedKeyword] = useState('');
  const [search, setSearch] = useState('');
  const detailPaneRef = useRef<HTMLElement>(null);
  const listPaneRef = useRef<HTMLElement>(null);
  const previousPanelMode = useRef<PanelMode>('idle');
  const videoRequest = useRef(0);

  useEffect(() => {
    const previousMode = previousPanelMode.current;
    previousPanelMode.current = panelMode;
    if (!window.matchMedia?.('(max-width: 900px)').matches) return;
    if (panelMode === 'idle' && previousMode === 'idle') return;
    const pane = panelMode === 'idle' ? listPaneRef.current : detailPaneRef.current;
    pane?.scrollIntoView?.({ block: 'start' });
    pane?.focus({ preventScroll: true });
  }, [panelMode, selectedId]);

  const loadAssignments = useCallback(async (topicId: number) => {
    setAssignmentLoading(true);
    try {
      const data = await youtubeTopicApi.listYoutubeTopicAssignments(topicId);
      setAssignedAccountIds(data.threadAccountIds ?? []);
    } catch {
      setAssignedAccountIds([]);
    } finally {
      setAssignmentLoading(false);
    }
  }, []);

  const searchVideos = useCallback(async (keyword: string) => {
    const request = ++videoRequest.current;
    const q = keyword.trim();
    if (!q) {
      setVideos([]);
      setVideosError(null);
      setSearchedKeyword('');
      setVideosLoading(false);
      return;
    }
    setVideosLoading(true);
    setVideosError(null);
    try {
      const data = await youtubeTopicApi.previewYoutubeVideos(q);
      if (request !== videoRequest.current) return;
      setVideos(data ?? []);
      setSearchedKeyword(q);
    } catch (err) {
      if (request !== videoRequest.current) return;
      setVideos([]);
      setSearchedKeyword(q);
      setVideosError(
        err instanceof ApiError
          ? err.message
          : '유튜브 검색에 실패했습니다.'
      );
    } finally {
      if (request === videoRequest.current) setVideosLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await youtubeTopicApi.listYoutubeTopics();
      setTopics(data);
      if (selectedId != null && !data.some((t) => t.id === selectedId)) {
        setSelectedId(null);
        setAssignedAccountIds([]);
        setPanelMode('idle');
        setForm(EMPTY_FORM);
      }
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : '유튜브 소재 목록을 불러오지 못했습니다.'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (panelMode !== 'detail' || selectedId == null) {
      if (panelMode !== 'detail') {
        setAssignedAccountIds([]);
      }
      return;
    }
    void loadAssignments(selectedId);
  }, [panelMode, selectedId, loadAssignments]);

  const openCreate = () => {
    setPanelMode('create');
    setSelectedId(null);
    setAssignedAccountIds([]);
    setForm(EMPTY_FORM);
    setError(null);
    setVideos([]);
    setVideosError(null);
    setSearchedKeyword('');
  };

  const openDetail = (topic: YoutubeTopic) => {
    setPanelMode('detail');
    setSelectedId(topic.id);
    setForm({
      name: topic.name ?? '',
      keyword: topic.keyword ?? '',
      pinOwnReplyEnabled: !!topic.pinOwnReplyEnabled,
      commentBrief: topic.commentBrief ?? '',
    });
    setError(null);
  };

  const closePanel = () => {
    setPanelMode('idle');
    setSelectedId(null);
    setAssignedAccountIds([]);
    setForm(EMPTY_FORM);
    setError(null);
    setVideos([]);
    setVideosError(null);
    setSearchedKeyword('');
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.keyword.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload: YoutubeTopicUpsertRequest = {
        name: form.name.trim(),
        keyword: form.keyword.trim(),
        pinOwnReplyEnabled: !!form.pinOwnReplyEnabled,
        commentBrief: form.commentBrief?.trim() || undefined,
      };
      const isCreate = panelMode === 'create';
      const saved = isCreate
        ? await youtubeTopicApi.createYoutubeTopic(payload)
        : await youtubeTopicApi.updateYoutubeTopic(selectedId!, payload);
      await load();
      openDetail(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (topic: YoutubeTopic) => {
    const ok = await confirm({
      title: '유튜브 소재 삭제',
      message: `"${topic.name}"을(를) 삭제할까요?`,
      confirmText: '삭제',
      cancelText: '취소',
    });
    if (!ok) return;
    try {
      await youtubeTopicApi.deleteYoutubeTopic(topic.id);
      if (selectedId === topic.id) closePanel();
      await load();
    } catch (err) {
      await alert({
        title: '삭제 실패',
        message: err instanceof ApiError ? err.message : '삭제에 실패했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    }
  };

  useEffect(() => {
    videoRequest.current += 1;
    setVideosLoading(false);
    if (panelMode !== 'create' && panelMode !== 'detail') {
      return;
    }
    const q = form.keyword.trim();
    if (q.length < 2) {
      setVideos([]);
      setVideosError(null);
      setSearchedKeyword('');
      return;
    }
    const delay = 400;
    const timer = window.setTimeout(() => {
      void searchVideos(q);
    }, delay);
    return () => { window.clearTimeout(timer); videoRequest.current += 1; };
  }, [form.keyword, panelMode, selectedId, searchVideos]);

  const toggleAccountAssignment = async (accountId: number, next: boolean) => {
    if (selectedId == null) return;
    const prev = assignedAccountIds;
    setAssignedAccountIds((ids) =>
      next
        ? ids.includes(accountId)
          ? ids
          : [...ids, accountId]
        : ids.filter((id) => id !== accountId)
    );
    setAssignmentSavingId(accountId);
    try {
      const data = next
        ? await youtubeTopicApi.assignYoutubeTopicAccount(selectedId, accountId)
        : await youtubeTopicApi.unassignYoutubeTopicAccount(
            selectedId,
            accountId
          );
      setAssignedAccountIds(data.threadAccountIds ?? []);
    } catch (err) {
      setAssignedAccountIds(prev);
      await alert({
        title: '배정 실패',
        message:
          err instanceof ApiError
            ? err.message
            : '계정 배정을 저장하지 못했습니다.',
        tone: 'danger',
        confirmText: '확인',
      });
    } finally {
      setAssignmentSavingId(null);
    }
  };

  const selected = topics.find((t) => t.id === selectedId);
  const searchTerm = search.trim().toLocaleLowerCase();
  const visibleTopics = topics.filter((topic) => [topic.name, topic.keyword].some((value) => value?.toLocaleLowerCase().includes(searchTerm)));

  return (
    <>
      {error && panelMode === 'idle' && <Alert tone="danger"><RecoveryRow><span>{error}</span><Button type="button" $variant="secondary" $size="sm" onClick={() => void load()}>다시 불러오기</Button></RecoveryRow></Alert>}
      <Board>
        <ListPane ref={listPaneRef} tabIndex={-1} aria-label="소재 목록" $detailOpen={panelMode !== 'idle'}>
          <ListHeader>
            <ListTitle>유튜브 소재</ListTitle>
            <HeaderSide>
              <ListCount>{searchTerm ? `${visibleTopics.length} / ${topics.length}` : topics.length}</ListCount>
              <Button type="button" $size="sm" onClick={openCreate}>
                <PlusIcon size={14} />
                추가
              </Button>
            </HeaderSide>
          </ListHeader>
          <SearchTools><Input type="search" aria-label="유튜브 소재 검색" placeholder="이름 또는 검색 키워드 찾기" value={search} onChange={(event) => setSearch(event.target.value)} /></SearchTools>
          {loading ? (
            <Center>
              <Spinner $size={22} />
            </Center>
          ) : topics.length === 0 ? (
            <EmptyState
              title="등록된 유튜브 소재가 없습니다"
              description="관심 키워드를 등록하고 영상을 활용할 계정을 배정하세요."
              action={<Button type="button" onClick={openCreate}><PlusIcon size={16} /> 첫 키워드 추가</Button>}
            />
          ) : visibleTopics.length === 0 ? (
            <EmptyState title="검색 결과가 없습니다" description="다른 이름이나 키워드로 검색해 보세요." action={<Button type="button" $variant="secondary" onClick={() => setSearch('')}>검색 초기화</Button>} />
          ) : (
            <TopicList>
              {visibleTopics.map((topic) => (
                <TopicItem
                  key={topic.id}
                  $active={panelMode === 'detail' && selectedId === topic.id}
                  aria-pressed={panelMode === 'detail' && selectedId === topic.id}
                  type="button"
                  onClick={() => openDetail(topic)}
                >
                  <TopicName>{topic.name}</TopicName>
                  <TopicMeta>검색어 · {topic.keyword}</TopicMeta>
                </TopicItem>
              ))}
            </TopicList>
          )}
        </ListPane>

        <DetailPane $idle={panelMode === 'idle'} ref={detailPaneRef} tabIndex={-1} aria-label={panelMode === 'create' ? '유튜브 소재 등록' : '유튜브 소재 상세'}>
          {panelMode === 'idle' && (
            <IdleState>
              <EmptyState
                title="유튜브 소재를 선택하세요"
                description="키워드별 검색 영상과 발행 계정을 관리할 수 있습니다."
                action={<Button type="button" $variant="secondary" onClick={openCreate}><PlusIcon size={16} /> 키워드 추가</Button>}
              />
            </IdleState>
          )}

          {(panelMode === 'create' ||
            (panelMode === 'detail' && selected)) && (
            <DetailScroll>
              <DetailHeader>
                <div>
                  <CardTitle>
                    {panelMode === 'create'
                      ? '유튜브 소재 등록'
                      : selected?.name}
                  </CardTitle>
                  {panelMode === 'detail' && selected && (
                    <DetailSub>검색 키워드 · {selected.keyword}</DetailSub>
                  )}
                </div>
                <DetailHeaderActions>
                  {panelMode === 'detail' && selected && (
                    <Button
                      type="button"
                      $variant="ghost"
                      $size="sm"
                      onClick={() => void handleDelete(selected)}
                    >
                      삭제
                    </Button>
                  )}
                  <Button
                    type="button"
                    $variant="ghost"
                    $size="sm"
                    onClick={closePanel}
                  >
                    목록으로
                  </Button>
                </DetailHeaderActions>
              </DetailHeader>

              {error && <Alert tone="danger">{error}</Alert>}

              <Form onSubmit={(e) => void handleSave(e)}>
                <Field label="이름" htmlFor="ytName" help="목록에 보일 이름">
                  <Input
                    id="ytName"
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="예: 알뜰 쇼핑 리뷰"
                    required
                  />
                </Field>
                <Field
                  label="유튜브 검색 키워드"
                  htmlFor="ytKeyword"
                  help="YouTube에서 이 키워드로 영상을 찾습니다"
                >
                  <Input
                    id="ytKeyword"
                    value={form.keyword}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, keyword: e.target.value }))
                    }
                    placeholder="예: 다이소 꿀템"
                    required
                  />
                </Field>
                <ToggleRow>
                  <div>
                    <ToggleLabel>발행 후 자동 댓글</ToggleLabel>
                    <ToggleHelp>
                      이 소재로 글이 나가면 첫 댓글과 팔로업 댓글을 예약합니다.
                      Threads 공식 API에 댓글 고정 기능이 없어 앱의 고정 배지는
                      달리지 않습니다. 계정에 댓글 페르소나가 있어야 동작합니다.
                    </ToggleHelp>
                  </div>
                  <Switch
                    checked={!!form.pinOwnReplyEnabled}
                    aria-label="발행 후 자동 댓글"
                    onChange={(next) =>
                      setForm((f) => ({ ...f, pinOwnReplyEnabled: next }))
                    }
                  />
                </ToggleRow>
                {form.pinOwnReplyEnabled && (
                  <Field
                    label="팔로업 댓글에 넣을 내용"
                    htmlFor="ytCommentBrief"
                    hint="선택"
                    help="링크나 추가 썰. 비워도 소재에 맞게 이어서 씁니다."
                  >
                    <Textarea
                      id="ytCommentBrief"
                      rows={3}
                      value={form.commentBrief ?? ''}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, commentBrief: e.target.value }))
                      }
                      placeholder="예: 영상에서 못 다한 팁, 관련 링크"
                      maxLength={2000}
                    />
                  </Field>
                )}
                <Actions>
                  <Button
                    type="submit"
                    disabled={
                      saving || !form.name.trim() || !form.keyword.trim()
                    }
                  >
                    {saving
                      ? <Spinner />
                      : panelMode === 'create'
                        ? '등록'
                        : '수정 저장'}
                  </Button>
                </Actions>
              </Form>

              <Section>
                <SectionTitleRow>
                  <SectionTitle>검색된 영상 {videos.length > 0 && <ResultCount>{videos.length}개</ResultCount>}</SectionTitle>
                  <Button
                    type="button"
                    $variant="ghost"
                    $size="sm"
                    disabled={videosLoading || form.keyword.trim().length < 2}
                    onClick={() => void searchVideos(form.keyword)}
                  >
                    {videosLoading ? <><Spinner $size={14} /> 검색 중</> : '다시 검색'}
                  </Button>
                </SectionTitleRow>
                <Hint>
                  키워드를 입력하면 바로 검색합니다. 배정된 계정은 이 목록의
                  영상 자막을 말투에 맞게 바꿔 올립니다.
                </Hint>
                {videosError && <Alert tone="danger">{videosError}</Alert>}
                {videosLoading && videos.length === 0 ? (
                  <Center>
                    <Spinner $size={22} />
                  </Center>
                ) : videos.length === 0 ? (
                  <EmptyState
                    title={
                      form.keyword.trim().length < 2
                        ? '검색 키워드를 입력하세요'
                        : searchedKeyword
                          ? '검색된 영상이 없습니다'
                          : '검색을 기다리는 중'
                    }
                    description={
                      form.keyword.trim().length < 2
                        ? '두 글자 이상 입력하면 YouTube에서 바로 찾습니다.'
                        : '키워드를 바꿔 보거나 잠시 후 다시 검색하세요.'
                    }
                  />
                ) : (
                  <VideoList aria-busy={videosLoading}>
                    {videos.map((video) => (
                      <VideoItem
                        key={video.videoId}
                        href={video.watchUrl ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`${video.title || video.videoId} · YouTube에서 새 탭으로 보기`}
                      >
                        {video.thumbnailUrl ? (
                          <VideoThumb src={video.thumbnailUrl} alt="" loading="lazy" decoding="async" />
                        ) : (
                          <VideoThumbFallback />
                        )}
                        <VideoBody>
                          <VideoTitle>{video.title || video.videoId}</VideoTitle>
                          <VideoMeta>
                            {[
                              video.channelTitle,
                              video.publishedAt
                                ? video.publishedAt.slice(0, 10)
                                : null,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </VideoMeta>
                        </VideoBody>
                      </VideoItem>
                    ))}
                  </VideoList>
                )}
              </Section>

              {panelMode === 'detail' && selected && (
                <Section>
                  <SectionTitle>예약 계정</SectionTitle>
                  <Hint>
                    켠 계정은 하루 소재 횟수 안에서 직접 소재와 함께 돌아가며
                    이 키워드 영상을 올립니다.
                  </Hint>
                  {accounts.length === 0 ? (
                    <EmptyState
                      title="연동된 계정이 없습니다"
                      description="계정 관리에서 Threads 계정을 연동하세요."
                    />
                  ) : assignmentLoading ? (
                    <Center>
                      <Spinner $size={22} />
                    </Center>
                  ) : (
                    <AccountList>
                      {accounts.map((account) => {
                        const profile = account.threadAccountProfileResponse;
                        const label = profile?.threadUserName
                          ? `@${profile.threadUserName}`
                          : `계정 #${account.id}`;
                        const checked = assignedAccountIds.includes(account.id);
                        return (
                          <AccountRow key={account.id}>
                            <AccountSelect>
                              <AccountLabel>{label}</AccountLabel>
                              <Switch
                                checked={checked}
                                disabled={assignmentSavingId != null}
                                aria-label={`${label} 배정`}
                                onChange={(next) =>
                                  void toggleAccountAssignment(account.id, next)
                                }
                              />
                            </AccountSelect>
                          </AccountRow>
                        );
                      })}
                    </AccountList>
                  )}
                </Section>
              )}
            </DetailScroll>
          )}
        </DetailPane>
      </Board>
    </>
  );
}

const RecoveryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;
const SearchTools = styled.div`
  padding: 12px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  input { background: ${({ theme }) => theme.colors.surface}; }
`;
const ResultCount = styled.span`
  margin-left: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
`;
const Board = styled.div`
  display: grid;
  grid-template-columns: minmax(240px, 320px) minmax(0, 1fr);
  gap: 0;
  min-height: min(70vh, 720px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

const ListPane = styled.aside<{ $detailOpen: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceMuted};

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    display: ${({ $detailOpen }) => $detailOpen ? 'none' : 'flex'};
    max-height: 460px;
  }
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const HeaderSide = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ListTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
`;

const ListCount = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 2px 8px;
`;

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
`;

const TopicItem = styled.button<{ $active?: boolean }>`
  text-align: left;
  padding: 17px 16px;
  min-height: 82px;
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  border-left: 3px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : 'transparent')};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.surface : 'transparent'};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const TopicName = styled.p`
  margin: 0;
  overflow-wrap: anywhere;
  line-height: 1.5;
  font-size: 14px;
  font-weight: 700;
`;

const TopicMeta = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailPane = styled.section<{ $idle: boolean }>`
  display: flex;
  flex-direction: column;
  @media (max-width: 900px) {
    display: ${({ $idle }) => $idle ? 'none' : 'flex'};
  }
  scroll-margin-top: 88px;
  min-width: 0;
  min-height: 0;
`;

const IdleState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: 24px;
`;

const DetailScroll = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  @media (max-width: 600px) { padding: 20px 16px; }
`;

const DetailHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const DetailHeaderActions = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 4px;
`;

const CardTitle = styled.h2`
  margin: 0;
  overflow-wrap: anywhere;
  font-size: 18px;
  font-weight: 700;
`;

const DetailSub = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  min-width: 0;
`;

const ToggleLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
`;

const ToggleHelp = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.7;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
`;

const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const VideoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VideoItem = styled.a`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  text-decoration: none;
  color: inherit;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.primary};
  }
  @media (max-width: 480px) { flex-direction: column; align-items: stretch; }
`;

const VideoThumb = styled.img`
  width: 120px;
  height: 68px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.border};
  @media (max-width: 480px) { width: 100%; height: auto; aspect-ratio: 16 / 9; }
`;

const VideoThumbFallback = styled.div`
  width: 120px;
  height: 68px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.border};
  @media (max-width: 480px) { width: 100%; height: auto; aspect-ratio: 16 / 9; }
`;

const VideoBody = styled.div`
  min-width: 0;
`;

const VideoTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 650;
  line-height: 1.55;
`;

const VideoMeta = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Hint = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.45;
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const AccountList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AccountRow = styled.div`
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surfaceMuted};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const AccountSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const AccountLabel = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;
